import { MutationButton } from "@Light/components/page/MutationButton";
import { PageBody } from "@Light/components/page/PageBody";
import { ErrorMessage, Field } from "@Light/components/system/Fieldset";
import { Input } from "@Light/components/system/Input";
import { ChevronDown } from "@untitledui/icons";
import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { FieldLabel } from "../FieldLabel";
import { useEnrollment } from "../flow/context";
import { useEnrollmentStepper } from "../flow/useEnrollmentStepper";
import { EnrollRoute } from "../routes";
import { useEnrollmentUtilityConfiguration } from "../useEnrollmentUtilityConfiguration";

type UtilityAccountNumberForm = {
  utility_account_number: string;
};

export const ManualUtilityAccountNumber = () => {
  const { requirements, next, back, nextMutation } = useEnrollment();
  const stepper = useEnrollmentStepper(EnrollRoute.ManualUtilityAccountNumber);
  const [helpOpen, setHelpOpen] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UtilityAccountNumberForm>();
  const utilConfig = useEnrollmentUtilityConfiguration();

  const onSubmit = handleSubmit(async (data) => {
    await next({
      utility_account_number: { value: data.utility_account_number.trim() },
    });
  });

  return (
    <form onSubmit={onSubmit}>
      <PageBody
        stepper={stepper}
        title={`What’s your ${utilConfig.accountNumberName}?`}
        subtitle="Your utility uses this number to identify the meter at your address."
        onBack={back}
        footer={
          <EnrollHelpButtonFooter>
            <MutationButton
              size="lg"
              mutation={nextMutation}
              mutateButtonText="Continue"
              errorMessage="Failed saving your utility account number. Please try again later"
              data-testid="manual-utility-account-number-submit"
            />
          </EnrollHelpButtonFooter>
        }
      >
        <div className="flex flex-col gap-4">
          <Field className="flex flex-col gap-1">
            <FieldLabel>{utilConfig.accountNumberName}</FieldLabel>
            <Input
              type="text"
              placeholder="12345-67890"
              isError={!!errors.utility_account_number}
              data-record="true"
              data-testid="manual-utility-account-number-input"
              {...register("utility_account_number", {
                required: true,
                value: requirements.utility_account_number?.value?.value ?? "",
              })}
            />
            <ErrorMessage error={errors.utility_account_number?.message} />
          </Field>

          {utilConfig.accountNumberDescription ? (
            <div className="-mx-lg flex flex-col">
              <button
                type="button"
                className="group flex gap-xs items-center justify-between text-left px-lg py-sm text-tertiary hover:text-primary"
                onClick={() => {
                  setHelpOpen(!helpOpen);
                }}
                data-testid="manual-utility-account-number-help"
              >
                <div className="text-xs font-medium">
                  Where do I find my {utilConfig.accountNumberName}?
                </div>
                <ChevronDown
                  className={clsx(
                    "size-3 stroke-[2.5px] shrink-0 text-fg-quaternary group-hover:text-tertiary transition-transform duration-300",
                    helpOpen && "rotate-180",
                  )}
                />
              </button>
              <div
                className={clsx(
                  "grid transition-[grid-template-rows] duration-300 ease-in-out",
                  helpOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                )}
              >
                <div className="overflow-hidden">
                  <div className="text-xs text-tertiary px-lg pb-md">
                    {utilConfig.accountNumberDescription}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </PageBody>
    </form>
  );
};
